import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Boxes,
  Building2,
  Check,
  ChevronRight,
  CircleDollarSign,
  FileText,
  Gauge,
  Layers3,
  Mail,
  Phone,
  QrCode,
  ReceiptText,
  Send,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  Users,
  Zap,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { api, formatIDR } from "@/lib/api";

const emptyContact = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
  website: "",
};

const featureCards = [
  {
    icon: ShoppingCart,
    title: "POS yang cepat dipakai",
    text: "Kasir Touch maupun Minimarket, barcode, diskon, metode tunai dan QRIS dalam alur transaksi yang ringkas.",
    className: "lg:col-span-2",
  },
  {
    icon: Boxes,
    title: "Stok real-time",
    text: "Pergerakan stok mengikuti penjualan, pembelian, refund, stock opname, dan aktivitas outlet.",
  },
  {
    icon: QrCode,
    title: "QRIS fleksibel",
    text: "Gunakan konfigurasi QRIS outlet sendiri atau skema QRIS yang disediakan owner sesuai kebutuhan bisnis.",
  },
  {
    icon: ReceiptText,
    title: "Akuntansi terintegrasi",
    text: "COA, jurnal, debit/kredit, biaya operasional, dan laporan membantu operasional lebih mudah ditelusuri.",
    className: "lg:col-span-2",
  },
  {
    icon: Building2,
    title: "Multi-outlet",
    text: "Kelola banyak outlet dalam satu akun dengan akses, pengguna, produk, dan laporan yang lebih terstruktur.",
  },
  {
    icon: ShieldCheck,
    title: "Role & audit trail",
    text: "Kontrol hak akses tim dan simpan jejak aktivitas untuk membantu pengelolaan operasional yang lebih disiplin.",
  },
];

const workflow = [
  { step: "01", title: "Buat outlet", text: "Siapkan struktur outlet dan pengguna sesuai kebutuhan operasional." },
  { step: "02", title: "Masukkan produk", text: "Atur produk, harga, stok, barcode, serta kebutuhan pembelian." },
  { step: "03", title: "Mulai transaksi", text: "Kasir dapat menerima pembayaran tunai maupun QRIS sesuai konfigurasi." },
  { step: "04", title: "Pantau bisnis", text: "Lihat penjualan, stok, laporan, jurnal, dan aktivitas dari satu workspace." },
];

function ProductPreview() {
  return (
    <div className="relative mx-auto w-full max-w-[680px]">
      <div className="absolute -inset-8 rounded-[3rem] bg-blue-600/10 blur-3xl" />
      <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_40px_100px_-40px_rgba(15,23,42,.35)]">
        <div className="flex h-12 items-center gap-2 border-b border-slate-200 px-5">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <div className="ml-3 rounded-full bg-slate-100 px-4 py-1.5 text-[10px] font-semibold text-slate-400">app.kraya.co.id</div>
        </div>
        <div className="grid min-h-[410px] grid-cols-[84px_1fr] sm:grid-cols-[170px_1fr]">
          <aside className="border-r border-slate-200 bg-slate-950 p-3 sm:p-5">
            <BrandMark size="sm" variant="on-dark" className="hidden sm:inline-flex" />
            <div className="mt-8 space-y-2">
              {[Gauge, ShoppingCart, Boxes, BarChart3, Users].map((Icon, index) => (
                <div key={index} className={`flex items-center gap-2 rounded-xl px-2.5 py-2.5 text-xs ${index === 0 ? "bg-white/10 text-white" : "text-slate-500"}`}>
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">{["Dashboard", "Kasir", "Stok", "Laporan", "Pengguna"][index]}</span>
                </div>
              ))}
            </div>
          </aside>
          <main className="bg-slate-50 p-4 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[.18em] text-blue-600">Ringkasan hari ini</div>
                <div className="mt-1 text-lg font-black text-slate-950 sm:text-2xl">Bisnis dalam satu layar.</div>
              </div>
              <div className="rounded-xl bg-white px-3 py-2 text-[10px] font-semibold text-slate-500 shadow-sm">Semua outlet</div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] text-slate-500">Penjualan</div>
                <div className="mt-2 text-lg font-black text-slate-950 sm:text-xl">Rp8,4 jt</div>
                <div className="mt-1 text-[10px] font-semibold text-emerald-600">+12,4%</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] text-slate-500">Transaksi</div>
                <div className="mt-2 text-lg font-black text-slate-950 sm:text-xl">184</div>
                <div className="mt-1 text-[10px] text-slate-400">hari ini</div>
              </div>
              <div className="col-span-2 rounded-2xl border border-slate-200 bg-white p-4 sm:col-span-1">
                <div className="text-[10px] text-slate-500">Outlet aktif</div>
                <div className="mt-2 text-lg font-black text-slate-950 sm:text-xl">4</div>
                <div className="mt-1 text-[10px] text-slate-400">terhubung</div>
              </div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-[1.25fr_.75fr]">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-slate-800">Tren penjualan</div>
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                </div>
                <div className="mt-6 flex h-24 items-end gap-2">
                  {[40, 58, 46, 72, 64, 82, 92].map((height, index) => (
                    <div key={index} className="flex-1 rounded-t-md bg-gradient-to-t from-blue-600 to-cyan-400" style={{ height: `${height}%` }} />
                  ))}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-950 p-4 text-white">
                <div className="text-[10px] uppercase tracking-[.15em] text-cyan-300">Status</div>
                <div className="mt-3 space-y-3 text-xs">
                  <div className="flex items-center justify-between"><span className="text-slate-400">QRIS</span><span className="font-bold text-emerald-300">Aktif</span></div>
                  <div className="flex items-center justify-between"><span className="text-slate-400">Stok</span><span className="font-bold">Realtime</span></div>
                  <div className="flex items-center justify-between"><span className="text-slate-400">Jurnal</span><span className="font-bold">Tercatat</span></div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <div className="absolute -bottom-6 -left-3 hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-xl sm:block">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><Check className="h-5 w-5" /></div>
          <div><div className="text-xs font-bold text-slate-950">Operasional tersinkron</div><div className="text-[10px] text-slate-500">Kasir · Stok · Laporan</div></div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [contact, setContact] = useState(emptyContact);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [contactError, setContactError] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get("/plans/public")
      .then((response) => {
        if (!mounted) return;
        setPlans((response.data || []).filter((plan) => plan.active !== false));
        setPlansError("");
      })
      .catch(() => {
        if (!mounted) return;
        setPlans([]);
        setPlansError("Daftar paket belum dapat dimuat. Silakan coba kembali beberapa saat lagi.");
      })
      .finally(() => {
        if (mounted) setPlansLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const featureCount = useMemo(() => {
    const keys = new Set();
    plans.forEach((plan) => (plan.feature_details || []).forEach((feature) => feature?.key && keys.add(feature.key)));
    return keys.size;
  }, [plans]);

  const updateContact = (field, value) => setContact((current) => ({ ...current, [field]: value }));

  const submitContact = async (event) => {
    event.preventDefault();
    setContactLoading(true);
    setContactMessage("");
    setContactError("");
    try {
      const response = await api.post("/contact", {
        ...contact,
        name: contact.name.trim(),
        business_name: contact.business_name.trim() || null,
        email: contact.email.trim(),
        phone: contact.phone.trim() || null,
        subject: contact.subject.trim(),
        message: contact.message.trim(),
      });
      setContactMessage(response.data?.message || "Pesan berhasil dikirim.");
      setContact(emptyContact);
    } catch (error) {
      setContactError(error?.response?.data?.detail || "Pesan belum dapat dikirim. Silakan coba kembali.");
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-slate-950">
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-18 max-w-7xl items-center justify-between px-5 py-4 lg:px-10">
          <Link to="/" aria-label="KRAYA home" data-testid="landing-brand"><BrandMark /></Link>
          <nav className="hidden items-center gap-7 text-sm font-semibold text-slate-600 lg:flex">
            <a href="#product" className="transition hover:text-slate-950">Produk</a>
            <a href="#features" className="transition hover:text-slate-950">Fitur</a>
            <a href="#pricing" className="transition hover:text-slate-950">Harga</a>
            <a href="#contact" className="transition hover:text-slate-950">Hubungi kami</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="landing-login-link" className="rounded-xl px-3 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-100 sm:px-4">Masuk</Link>
            <Link to="/register" data-testid="landing-register-cta" className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-slate-950/10 transition hover:-translate-y-0.5 hover:bg-blue-600 sm:px-5">
              Coba gratis <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section id="product" className="relative overflow-hidden border-b border-slate-200 bg-[radial-gradient(circle_at_85%_15%,rgba(37,99,235,.10),transparent_30%),radial-gradient(circle_at_15%_65%,rgba(34,211,238,.10),transparent_28%)]">
          <div className="mx-auto grid max-w-7xl gap-14 px-5 py-16 sm:py-20 lg:grid-cols-[.9fr_1.1fr] lg:items-center lg:px-10 lg:py-28">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
                <Sparkles className="h-3.5 w-3.5" /> POS multi-outlet untuk bisnis modern
              </div>
              <h1 className="mt-7 max-w-3xl text-5xl font-black leading-[.94] tracking-[-0.055em] text-slate-950 sm:text-6xl lg:text-7xl">
                Satu sistem untuk <span className="text-blue-600">kasir, stok, QRIS</span> dan keuangan.
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-600">
                KRAYA membantu bisnis mengelola transaksi, produk, stok, pembelian, pembayaran QRIS, laporan, jurnal, pengguna, dan banyak outlet dari satu workspace yang lebih sederhana.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-cta-trial" className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-xl shadow-blue-600/20 transition hover:-translate-y-0.5 hover:bg-blue-700">
                  Mulai trial 14 hari <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#features" className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-6 py-3.5 text-sm font-bold text-slate-800 transition hover:border-slate-400 hover:bg-slate-50">
                  Lihat kemampuan <ChevronRight className="h-4 w-4" />
                </a>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-600">
                {["Tanpa kartu kredit", "Setup cepat", "Cocok untuk multi-outlet"].map((item) => (
                  <div key={item} className="flex items-center gap-2"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"><Check className="h-3.5 w-3.5" /></span>{item}</div>
                ))}
              </div>
            </div>
            <ProductPreview />
          </div>
        </section>

        <section className="border-b border-slate-200 bg-slate-950 text-white">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-white/10 px-0 sm:grid-cols-4 lg:px-10">
            {[
              [Store, "Multi-outlet", "Satu workspace"],
              [Layers3, featureCount ? `${featureCount}+ fitur` : "Fitur lengkap", "Mengikuti paket aktif"],
              [QrCode, "QRIS", "Konfigurasi fleksibel"],
              [ShieldCheck, "Role & audit", "Kontrol tim"],
            ].map(([Icon, title, subtitle]) => (
              <div key={title} className="bg-slate-950 px-5 py-7 sm:px-7">
                <Icon className="h-5 w-5 text-cyan-300" />
                <div className="mt-3 text-lg font-black">{title}</div>
                <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
              </div>
            ))}
          </div>
        </section>

        <section id="features" className="bg-white py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-5 lg:px-10">
            <div className="grid gap-8 lg:grid-cols-[.8fr_1.2fr] lg:items-end">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Dibuat untuk operasional nyata</div>
                <h2 className="mt-4 text-4xl font-black tracking-[-0.045em] sm:text-5xl">Lebih dari sekadar aplikasi kasir.</h2>
              </div>
              <p className="max-w-2xl text-lg leading-8 text-slate-600 lg:justify-self-end">
                Dari transaksi di depan kasir sampai monitoring di belakang layar, KRAYA menyatukan fungsi yang biasanya tersebar di banyak alat.
              </p>
            </div>
            <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {featureCards.map(({ icon: Icon, title, text, className = "" }) => (
                <article key={title} className={`group rounded-[2rem] border border-slate-200 bg-slate-50 p-7 transition hover:-translate-y-1 hover:border-blue-200 hover:bg-white hover:shadow-xl hover:shadow-slate-900/5 ${className}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-blue-600 shadow-sm ring-1 ring-slate-200 transition group-hover:bg-blue-600 group-hover:text-white"><Icon className="h-5 w-5" /></div>
                  <h3 className="mt-7 text-xl font-black tracking-[-0.025em]">{title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-slate-50 py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-5 lg:px-10">
            <div className="grid gap-12 lg:grid-cols-[.9fr_1.1fr] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1.5 text-xs font-bold uppercase tracking-[.16em] text-blue-700"><Zap className="h-3.5 w-3.5" /> Alur lebih ringkas</div>
                <h2 className="mt-5 text-4xl font-black tracking-[-0.045em] sm:text-5xl">Dari buka outlet sampai laporan, tetap dalam satu alur.</h2>
                <p className="mt-5 max-w-xl text-lg leading-8 text-slate-600">Kurangi perpindahan antar aplikasi dan bantu tim bekerja dengan data operasional yang sama.</p>
                <div className="mt-8 grid grid-cols-2 gap-3">
                  {[
                    [CircleDollarSign, "Transaksi & refund"],
                    [Boxes, "Stok & pembelian"],
                    [FileText, "Jurnal & laporan"],
                    [Users, "Role & pengguna"],
                  ].map(([Icon, text]) => (
                    <div key={text} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm font-bold text-slate-800"><Icon className="h-5 w-5 text-blue-600" />{text}</div>
                  ))}
                </div>
              </div>
              <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/5 sm:p-7">
                {workflow.map((item, index) => (
                  <div key={item.step} className={`grid grid-cols-[52px_1fr] gap-4 py-5 ${index !== workflow.length - 1 ? "border-b border-slate-200" : ""}`}>
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 text-xs font-black text-white">{item.step}</div>
                    <div><h3 className="font-black text-slate-950">{item.title}</h3><p className="mt-1 text-sm leading-6 text-slate-500">{item.text}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="pricing" className="bg-white py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-5 lg:px-10">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Paket berlangganan</div>
                <h2 className="mt-4 max-w-3xl text-4xl font-black tracking-[-0.045em] sm:text-5xl">Mulai sesuai skala bisnis Anda.</h2>
                <p className="mt-4 max-w-2xl leading-7 text-slate-600">Paket dan harga berikut dimuat langsung dari konfigurasi KRAYA yang aktif.</p>
              </div>
              <div className="inline-flex w-fit rounded-2xl border border-slate-200 bg-slate-50 p-1">
                <button type="button" onClick={() => setBillingCycle("monthly")} className={`rounded-xl px-4 py-2 text-sm font-bold transition ${billingCycle === "monthly" ? "bg-slate-950 text-white" : "text-slate-500 hover:text-slate-950"}`}>Bulanan</button>
                <button type="button" onClick={() => setBillingCycle("yearly")} className={`rounded-xl px-4 py-2 text-sm font-bold transition ${billingCycle === "yearly" ? "bg-slate-950 text-white" : "text-slate-500 hover:text-slate-950"}`}>Tahunan</button>
              </div>
            </div>

            {plansLoading && <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="public-plans-loading">{[0, 1, 2].map((item) => <div key={item} className="h-[470px] animate-pulse rounded-[2rem] bg-slate-100" />)}</div>}
            {!plansLoading && plansError && <div className="mt-10 rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700" data-testid="public-plans-error">{plansError}</div>}
            {!plansLoading && !plansError && plans.length === 0 && <div className="mt-10 rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">Belum ada paket aktif yang tersedia.</div>}

            {!plansLoading && !plansError && plans.length > 0 && (
              <div className="mt-10 grid items-stretch gap-5 md:grid-cols-2 xl:grid-cols-3">
                {plans.map((plan, index) => {
                  const price = billingCycle === "yearly" ? plan.price_yearly : plan.price_monthly;
                  const featured = index === Math.min(1, plans.length - 1);
                  return (
                    <article key={plan.id} className={`relative flex flex-col rounded-[2rem] border p-7 transition hover:-translate-y-1 ${featured ? "border-blue-600 bg-slate-950 text-white shadow-2xl shadow-blue-950/15" : "border-slate-200 bg-white text-slate-950 shadow-sm"}`} data-testid={`public-plan-${plan.id}`}>
                      {featured && <div className="absolute right-6 top-6 rounded-full bg-blue-600 px-3 py-1 text-[10px] font-black uppercase tracking-[.14em] text-white">Pilihan populer</div>}
                      <div className={`text-xs font-bold uppercase tracking-[.18em] ${featured ? "text-cyan-300" : "text-blue-600"}`}>Paket</div>
                      <h3 className="mt-2 text-3xl font-black tracking-[-0.04em]">{plan.name}</h3>
                      <div className="mt-7"><span className="text-4xl font-black tracking-[-0.05em] tabular-nums">{formatIDR(price)}</span><span className={`ml-1 text-sm ${featured ? "text-slate-400" : "text-slate-500"}`}>/{billingCycle === "yearly" ? "tahun" : "bulan"}</span></div>
                      <div className="mt-6 grid grid-cols-2 gap-3">
                        <div className={`rounded-2xl p-4 ${featured ? "bg-white/10" : "bg-slate-50"}`}><div className={`text-xs ${featured ? "text-slate-400" : "text-slate-500"}`}>Outlet</div><div className="mt-1 text-2xl font-black">{plan.max_outlets}</div></div>
                        <div className={`rounded-2xl p-4 ${featured ? "bg-white/10" : "bg-slate-50"}`}><div className={`text-xs ${featured ? "text-slate-400" : "text-slate-500"}`}>Pengguna</div><div className="mt-1 text-2xl font-black">{plan.max_users}</div></div>
                      </div>
                      <div className={`my-6 h-px ${featured ? "bg-white/10" : "bg-slate-200"}`} />
                      <ul className="flex-1 space-y-3 text-sm">
                        {(plan.feature_details || []).slice(0, 7).map((feature) => (
                          <li key={feature.key || feature.label} className="flex items-start gap-2.5"><Check className={`mt-0.5 h-4 w-4 shrink-0 ${featured ? "text-cyan-300" : "text-emerald-600"}`} /><span className={featured ? "text-slate-300" : "text-slate-600"}>{feature.label}</span></li>
                        ))}
                      </ul>
                      {(plan.feature_details || []).length > 7 && <div className={`mt-3 text-xs font-semibold ${featured ? "text-slate-500" : "text-slate-400"}`}>+ {(plan.feature_details || []).length - 7} fitur lainnya</div>}
                      <Link to={`/register?plan=${encodeURIComponent(plan.id)}`} data-testid={`pricing-cta-${String(plan.name || plan.id).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-bold transition ${featured ? "bg-white text-slate-950 hover:bg-cyan-50" : "bg-slate-950 text-white hover:bg-blue-600"}`}>
                        Pilih {plan.name} <ArrowRight className="h-4 w-4" />
                      </Link>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-10 lg:pb-28">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-blue-600 px-6 py-12 text-white sm:px-10 lg:grid lg:grid-cols-[1fr_auto] lg:items-center lg:px-14 lg:py-14">
            <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-cyan-300/20 blur-3xl" />
            <div className="relative">
              <div className="text-xs font-bold uppercase tracking-[.18em] text-blue-100">Siap mencoba?</div>
              <h2 className="mt-3 max-w-3xl text-3xl font-black tracking-[-0.04em] sm:text-4xl">Buat workspace KRAYA dan mulai rapikan operasional bisnis Anda.</h2>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-blue-100">Trial 14 hari dimulai setelah email Anda berhasil diverifikasi.</p>
            </div>
            <Link to="/register" className="relative mt-7 inline-flex items-center gap-2 rounded-2xl bg-white px-6 py-3.5 text-sm font-black text-blue-700 transition hover:-translate-y-0.5 hover:bg-cyan-50 lg:mt-0">Mulai gratis <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </section>

        <section id="contact" className="border-t border-slate-200 bg-slate-50 py-20 lg:py-28">
          <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[.8fr_1.2fr] lg:px-10">
            <div>
              <div className="text-xs font-bold uppercase tracking-[.2em] text-blue-600">Hubungi KRAYA</div>
              <h2 className="mt-4 text-4xl font-black tracking-[-0.045em] sm:text-5xl">Punya kebutuhan yang lebih spesifik?</h2>
              <p className="mt-5 max-w-lg text-lg leading-8 text-slate-600">Ceritakan jumlah outlet, pola operasional, atau kebutuhan pembayaran Anda. Tim KRAYA dapat menindaklanjuti dari informasi yang dikirim.</p>
              <div className="mt-8 space-y-3 text-sm text-slate-700">
                <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-200"><Mail className="h-4 w-4 text-blue-600" /></div>Diskusi kebutuhan bisnis</div>
                <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-200"><Phone className="h-4 w-4 text-blue-600" /></div>Demo dan onboarding</div>
                <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-200"><Building2 className="h-4 w-4 text-blue-600" /></div>Kebutuhan multi-outlet</div>
              </div>
            </div>
            <form onSubmit={submitContact} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/5 sm:p-8" data-testid="landing-contact-form">
              <input tabIndex="-1" autoComplete="off" aria-hidden="true" className="hidden" value={contact.website} onChange={(event) => updateContact("website", event.target.value)} />
              <div className="grid gap-5 sm:grid-cols-2">
                <div><label className="mb-2 block text-sm font-bold text-slate-700">Nama</label><input required value={contact.name} onChange={(event) => updateContact("name", event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Nama Anda" /></div>
                <div><label className="mb-2 block text-sm font-bold text-slate-700">Nama bisnis</label><input value={contact.business_name} onChange={(event) => updateContact("business_name", event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Nama usaha" /></div>
                <div><label className="mb-2 block text-sm font-bold text-slate-700">Email</label><input type="email" required value={contact.email} onChange={(event) => updateContact("email", event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="nama@bisnis.com" /></div>
                <div><label className="mb-2 block text-sm font-bold text-slate-700">Nomor HP</label><input value={contact.phone} onChange={(event) => updateContact("phone", event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="08xxxxxxxxxx" /></div>
              </div>
              <div className="mt-5"><label className="mb-2 block text-sm font-bold text-slate-700">Subjek</label><input required value={contact.subject} onChange={(event) => updateContact("subject", event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Contoh: Demo KRAYA untuk 5 outlet" /></div>
              <div className="mt-5"><label className="mb-2 block text-sm font-bold text-slate-700">Pesan</label><textarea required rows={5} value={contact.message} onChange={(event) => updateContact("message", event.target.value)} className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Ceritakan kebutuhan bisnis Anda..." /></div>
              {contactMessage && <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="landing-contact-success">{contactMessage}</div>}
              {contactError && <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="landing-contact-error">{contactError}</div>}
              <button type="submit" disabled={contactLoading} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3.5 text-sm font-bold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50">
                <Send className="h-4 w-4" /> {contactLoading ? "Mengirim..." : "Kirim pesan"}
              </button>
            </form>
          </div>
        </section>
      </main>

      <footer className="bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 md:grid-cols-4 lg:px-10">
          <div className="md:col-span-2">
            <BrandMark size="lg" variant="on-dark" />
            <p className="mt-4 max-w-md text-sm leading-7 text-slate-400">Platform POS multi-outlet untuk membantu bisnis mengelola transaksi, stok, QRIS, laporan, keuangan, dan tim dalam satu workspace.</p>
          </div>
          <div><div className="text-xs font-bold uppercase tracking-[.2em] text-slate-500">Produk</div><div className="mt-4 space-y-3 text-sm text-slate-300"><a className="block hover:text-white" href="#features">Fitur</a><a className="block hover:text-white" href="#pricing">Harga</a><a className="block hover:text-white" href="#contact">Hubungi kami</a></div></div>
          <div><div className="text-xs font-bold uppercase tracking-[.2em] text-slate-500">Akun</div><div className="mt-4 space-y-3 text-sm text-slate-300"><Link className="block hover:text-white" to="/login">Masuk</Link><Link className="block hover:text-white" to="/register">Daftar mitra</Link></div></div>
        </div>
        <div className="border-t border-white/10 px-5 py-5 text-center text-xs text-slate-500">© {new Date().getFullYear()} KRAYA. <span className="text-slate-300">Powered By AntSoft ID</span></div>
      </footer>
    </div>
  );
}
